/**
 * The form primitives the correspondent detail tabs share, so Profile and
 * Pricing read as one system: the 12-column field grid, its cells, the input
 * styling and the titled section card. Both tabs lay these out inside
 * `PricingPageLayout` (content column + "On this page" rail).
 */
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CheckRounded from "@mui/icons-material/CheckRounded";

import { PricingSection } from "../../admin/AdminPricingScreen/pricingUi";
import { slate, status, teal } from "../../../theme/colors";
import { FIELD_TEXT } from "../../../theme/formStyles";

export const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    fontSize: "13px",
    background: "#fff",
  },
};

/**
 * 12-column grid. Rows are 24px apart (the 2026-10-08 field standard — CLAUDE.md
 * "Form field labels & helpers"); columns stay 16px.
 */
export function Grid({ children }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
        columnGap: 2,
        rowGap: 3,
      }}
    >
      {children}
    </Box>
  );
}

export function Cell({ span = 12, spanSm, children }) {
  return (
    <Box
      sx={{
        minWidth: 0,
        gridColumn: {
          xs: "span 12",
          sm: `span ${spanSm ?? span}`,
          md: `span ${span}`,
        },
      }}
    >
      {children}
    </Box>
  );
}

/**
 * The correspondent drilldown's ONE checkbox: an 18px rounded box (white with a
 * slate border at rest, filled teal-600 with a white check when ticked) and a
 * 14px label beside it — the whole row is the hit target. Use this rather than
 * a bare MUI `<Checkbox>` anywhere on the correspondent tabs.
 */
const boxBase = {
  width: 18,
  height: 18,
  borderRadius: "5px",
  boxSizing: "border-box",
  display: "grid",
  placeItems: "center",
  transition: "background-color .12s, border-color .12s",
};

export function CorrespondentCheckbox({ label, checked, onChange, disabled }) {
  return (
    <Box
      component="label"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1.25,
        minHeight: 32,
        cursor: disabled ? "default" : "pointer",
        userSelect: "none",
        "&:hover .cdCheckBox": disabled
          ? undefined
          : { borderColor: checked ? teal[700] : slate[400] },
      }}
    >
      <Checkbox
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        disableRipple
        icon={
          <Box
            className="cdCheckBox"
            sx={{ ...boxBase, border: `1.5px solid ${slate[300]}`, background: "#fff" }}
          />
        }
        checkedIcon={
          <Box
            className="cdCheckBox"
            sx={{
              ...boxBase,
              border: `1.5px solid ${teal[600]}`,
              background: teal[600],
            }}
          >
            <CheckRounded sx={{ fontSize: 14, color: "#fff" }} />
          </Box>
        }
        sx={{
          p: 0,
          "&.Mui-focusVisible .cdCheckBox": {
            outline: `2px solid ${teal[200]}`,
            outlineOffset: "2px",
          },
        }}
      />
      <Box
        component="span"
        sx={{ fontSize: 14, color: disabled ? slate[400] : slate[900] }}
      >
        {label}
      </Box>
    </Box>
  );
}

/**
 * Heading over a group of controls (NDA, Prospect questionnaire on file,
 * Executed agreement…) — styled exactly like a field label (FIELD_TEXT.label)
 * so it reads as one with the labelled inputs beside it.
 */
export function ColumnHead({ children }) {
  return (
    // Same 4px gap to its control as every field label (FIELD_TEXT.label).
    <Box sx={FIELD_TEXT.label}>
      {children}
    </Box>
  );
}

const BADGE_TONES = {
  green: status.success,
  amber: status.warning,
  slate: status.neutral,
};

/** Header status pill for a section card (dot + text), e.g. "Sent Oct 23 · awaiting". */
export function SectionBadge({ tone = "slate", children }) {
  const t = BADGE_TONES[tone] || BADGE_TONES.slate;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        borderRadius: "999px",
        px: 1.25,
        py: "3px",
        fontSize: "12px",
        fontWeight: 500,
        whiteSpace: "nowrap",
        background: t.bg,
        color: t.fg,
      }}
    >
      <Box
        component="span"
        sx={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }}
      />
      {children}
    </Box>
  );
}

// The correspondent cards are dense forms — they read better with more air
// between the header block and the first field row than the pricing screens'
// default.
export const ProfileSection = (props) => (
  <PricingSection headerGap={3} {...props} />
);
